/** Cài đặt số chữ số thập phân khi hiển thị — lưu localStorage (cục bộ trình duyệt). */

/**
 * Locale cố định cho mọi format số / tiền trên FE.
 * `vi-VN`: dấu `.` = hàng nghìn, `,` = thập phân — không phụ thuộc OS hay ngôn ngữ UI.
 */
export const NUMBER_DISPLAY_LOCALE = 'vi-VN'

export type NumberDisplayFormatPrefKey =
  | 'vnd'
  | 'usd'
  | 'rmb'
  | 'weight'
  | 'volume'
  | 'general'

export type NumberDisplayFormatPrefs = Record<NumberDisplayFormatPrefKey, number>

export const DEFAULT_NUMBER_DISPLAY_FORMAT_PREFS: NumberDisplayFormatPrefs = {
  vnd: 0,
  usd: 6,
  rmb: 6,
  weight: 2,
  volume: 6,
  general: 6,
}

export const NUMBER_DISPLAY_FORMAT_PREF_KEYS: NumberDisplayFormatPrefKey[] = [
  'vnd',
  'usd',
  'rmb',
  'weight',
  'volume',
  'general',
]

export const NUMBER_DISPLAY_FORMAT_STORAGE_KEY = 'banhang-number-display-format-v1'
export const NUMBER_DISPLAY_FORMAT_CHANGE_EVENT = 'banhang-number-display-format-change'

const MIN_DECIMALS = 0
const MAX_DECIMALS = 9

let cache: NumberDisplayFormatPrefs | null = null

function clampDecimals(value: unknown, fallback: number): number {
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return fallback
  return Math.min(MAX_DECIMALS, Math.max(MIN_DECIMALS, Math.round(n)))
}

function normalizePrefs(raw: Partial<NumberDisplayFormatPrefs> | null | undefined): NumberDisplayFormatPrefs {
  const base = { ...DEFAULT_NUMBER_DISPLAY_FORMAT_PREFS }
  if (!raw || typeof raw !== 'object') return base
  for (const key of NUMBER_DISPLAY_FORMAT_PREF_KEYS) {
    if (raw[key] != null) {
      base[key] = clampDecimals(raw[key], DEFAULT_NUMBER_DISPLAY_FORMAT_PREFS[key])
    }
  }
  return base
}

function readFromStorage(): NumberDisplayFormatPrefs {
  if (typeof window === 'undefined') return { ...DEFAULT_NUMBER_DISPLAY_FORMAT_PREFS }
  try {
    const raw = window.localStorage.getItem(NUMBER_DISPLAY_FORMAT_STORAGE_KEY)
    if (!raw) return { ...DEFAULT_NUMBER_DISPLAY_FORMAT_PREFS }
    return normalizePrefs(JSON.parse(raw) as Partial<NumberDisplayFormatPrefs>)
  } catch {
    return { ...DEFAULT_NUMBER_DISPLAY_FORMAT_PREFS }
  }
}

export function getNumberDisplayFormatPrefs(): NumberDisplayFormatPrefs {
  if (!cache) cache = readFromStorage()
  return cache
}

export function getNumberDisplayDecimals(key: NumberDisplayFormatPrefKey): number {
  return getNumberDisplayFormatPrefs()[key]
}

export function setNumberDisplayFormatPrefs(next: NumberDisplayFormatPrefs): NumberDisplayFormatPrefs {
  const normalized = normalizePrefs(next)
  cache = normalized
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(NUMBER_DISPLAY_FORMAT_STORAGE_KEY, JSON.stringify(normalized))
    } catch {
      /* ignore quota / private mode */
    }
    window.dispatchEvent(new Event(NUMBER_DISPLAY_FORMAT_CHANGE_EVENT))
  }
  return normalized
}

/** Chỉ đổi cache tạm (không ghi localStorage) — dùng cho preview UI. */
export function withNumberDisplayFormatPrefsOverride<T>(
  next: NumberDisplayFormatPrefs,
  fn: () => T,
): T {
  const prev = getNumberDisplayFormatPrefs()
  cache = normalizePrefs(next)
  try {
    return fn()
  } finally {
    cache = prev
  }
}

export function resetNumberDisplayFormatPrefs(): NumberDisplayFormatPrefs {
  return setNumberDisplayFormatPrefs({ ...DEFAULT_NUMBER_DISPLAY_FORMAT_PREFS })
}

export function invalidateNumberDisplayFormatPrefsCache(): void {
  cache = null
}

/** Preview format số theo prefs + `NUMBER_DISPLAY_LOCALE`. `vnd` cố định số lẻ; còn lại bỏ 0 thừa. */
export function previewNumberByPrefKey(
  value: number,
  key: NumberDisplayFormatPrefKey,
  prefs?: NumberDisplayFormatPrefs,
): string {
  const decimals = (prefs ?? getNumberDisplayFormatPrefs())[key]
  if (key === 'vnd') {
    return new Intl.NumberFormat(NUMBER_DISPLAY_LOCALE, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value)
  }
  return new Intl.NumberFormat(NUMBER_DISPLAY_LOCALE, {
    maximumFractionDigits: decimals,
  }).format(value)
}
